import { zodResolver } from "@hookform/resolvers/zod"
import { DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useMemo } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useQuery } from "urql"

import { Button } from "@/components/common/Button"
import { ExternalLocationType, GetCompanyProductAssociationListDocument } from "@/graphql/codegen/graphql"
import { useGetLocations } from "@/hooks/useGetLocations"
import { LocationRowFields } from "@/screens/CustomerProducts/components/AssociateProductDialog/LocationRowFields"
import { EMPTY_LOCATION } from "@/screens/CustomerProducts/constants"
import { useAddAssociationLocation } from "@/screens/CustomerProducts/hooks/useAddAssociationLocation"
import {
  type AssociationLocationFormValues,
  createAssociationLocationFormSchema,
} from "@/screens/Products/schemas/associateProductForm"
import { AddressFormat } from "@/types/common"
import type { Option } from "@/types/form-fields"
import { formatLocationAddress } from "@/utils/utils"

const SHIP_TO_LOCATION_TYPES = [ExternalLocationType.Ship]

interface AddAssociationLocationContentProps {
  companyProductAssociationId: string
  companyId: string
  productId: number
  customerCodes: string[]
  initialShipToLocationId?: string
  onClose: () => void
  onSuccess: () => void
}

export const AddAssociationLocationContent: FC<AddAssociationLocationContentProps> = ({
  companyProductAssociationId,
  companyId,
  productId,
  customerCodes,
  initialShipToLocationId,
  onClose,
  onSuccess,
}) => {
  const [{ data: associationsData }] = useQuery({
    query: GetCompanyProductAssociationListDocument,
    variables: { input: { companyId, limit: 200, filter: { productId } } },
  })

  const { locations: allLocations } = useGetLocations(companyId, {
    isLocationActive: true,
    externalLocationTypes: SHIP_TO_LOCATION_TYPES,
  })

  const existingLocationIds = useMemo(
    () =>
      (associationsData?.companyProductAssociation?.list?.items ?? [])
        .map((item) => item.shipToLocationId)
        .filter((id): id is string => id !== null),
    [associationsData]
  )

  const schema = useMemo(() => createAssociationLocationFormSchema(existingLocationIds), [existingLocationIds])

  const methods = useForm<AssociationLocationFormValues>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      locations: [{ ...EMPTY_LOCATION, shipToLocationId: initialShipToLocationId ?? "" }],
    },
  })

  const {
    formState: { isValid, isDirty, isSubmitting },
    watch,
  } = methods

  const locations = watch("locations")

  const locationOptions = useMemo<Option<string>[]>(
    () =>
      allLocations.map((loc) => ({
        value: loc.locationId,
        label: formatLocationAddress(loc, AddressFormat.Medium),
      })),
    [allLocations]
  )

  const { addLocation, isAdding } = useAddAssociationLocation()

  // A prefilled ship-to is already a valid starting point, so the untouched form is submittable.
  const canSubmit =
    isValid &&
    (isDirty || Boolean(initialShipToLocationId)) &&
    !isSubmitting &&
    !isAdding &&
    !!locations[0]?.shipToLocationId

  const handleSave = methods.handleSubmit(async (formValues: AssociationLocationFormValues) => {
    const loc = formValues.locations[0]
    if (!loc) return

    const success = await addLocation({
      companyProductAssociationId,
      shipToLocationId: loc.shipToLocationId,
      customerCode: loc.customerCode?.length ? loc.customerCode : undefined,
      priorityStatus: loc.priorityStatus ?? undefined,
      approvalStatus: loc.approvalStatus ?? undefined,
    })

    if (success) {
      onSuccess()
    }
  })

  return (
    <>
      <DialogContent classes={{ root: "p-10" }}>
        <FormProvider {...methods}>
          <LocationRowFields
            index={0}
            locationOptions={locationOptions}
            customerCodes={customerCodes}
            disableShipTo={Boolean(initialShipToLocationId)}
          />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={onClose}>
          Cancel
        </Button>
        <Button appearance='contained' onClick={handleSave} disabled={!canSubmit}>
          Save
        </Button>
      </DialogActions>
    </>
  )
}
